import React, { useEffect, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../../firebase-config";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../assets/styles/ClientShared.css";

const initialFormData = {
  businessName: "",
  shortDescription: "",
  services: "",
  serviceArea: "",
  phone: "",
  email: "",
  businessHours: "",
  currentWebsite: "",
  socialLinks: "",
  preferredColors: "",
  referenceSites: "",
  mustHaveContent: "",
  avoid: "",
};

const fields = [
  {
    title: "Business Basics",
    description: "The essentials needed to make the site accurate.",
    items: [
      { name: "businessName", label: "Business name", type: "input", required: true },
      { name: "shortDescription", label: "What does the business do? Keep it short.", type: "textarea", required: true },
      { name: "services", label: "Main services or products", type: "textarea", placeholder: "List the services you want on the site.", required: true },
      { name: "serviceArea", label: "Service area", type: "input", placeholder: "Example: Philadelphia, Bucks County, South Jersey" },
    ],
  },
  {
    title: "Contact Details",
    description: "What customers should use to reach you.",
    items: [
      { name: "phone", label: "Public phone number", type: "input" },
      { name: "email", label: "Public email", type: "input" },
      { name: "businessHours", label: "Business hours", type: "textarea" },
      { name: "currentWebsite", label: "Current website, if any", type: "input" },
      { name: "socialLinks", label: "Social links", type: "textarea", placeholder: "Instagram, Facebook, Google Business Profile, etc." },
    ],
  },
  {
    title: "Design Direction",
    description: "Light guidance so we can style the site quickly.",
    items: [
      { name: "preferredColors", label: "Preferred colors", type: "input", placeholder: "Example: black, white, gold" },
      { name: "referenceSites", label: "Reference websites you like", type: "textarea", placeholder: "Paste links and what you like about them." },
      { name: "mustHaveContent", label: "Anything that must be included", type: "textarea" },
      { name: "avoid", label: "Anything to avoid", type: "textarea" },
    ],
  },
];

const StarterIntake = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [projectId, setProjectId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error("User not authenticated.");

        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists()) throw new Error("User document not found.");

        const userData = userSnap.data();
        if (!userData.projectId) throw new Error("Project ID not found.");

        setProjectId(userData.projectId);
        const projectRef = doc(db, "projects", userData.projectId);
        const projectSnap = await getDoc(projectRef);

        if (projectSnap.exists()) {
          setFormData({ ...initialFormData, ...(projectSnap.data().starterIntake || {}) });
        }
      } catch (err) {
        setError(err.message);
        toast.error("Failed to load Starter intake.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.businessName || !formData.shortDescription || !formData.services) {
      setError("Please complete the required business basics before saving.");
      toast.warn("Please complete the required fields.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      if (!projectId) throw new Error("Project ID not set.");
      await updateDoc(doc(db, "projects", projectId), {
        starterIntake: formData,
      });
      toast.success("Starter intake saved.");
    } catch (err) {
      setError("Failed to save intake: " + err.message);
      toast.error("Failed to save Starter intake.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="cp-loading"><p>Loading Starter intake...</p></div>;
  }

  return (
    <div className="cp-page cp-page--narrow">
      <div className="cp-page-header">
        <p className="cp-eyebrow">Client Portal</p>
        <h1 className="cp-title">Starter Intake</h1>
        <p className="cp-subtitle">
          Answer the essentials once. We will use this to write and build your 4-page site without making you draft every page yourself.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {fields.map((section) => (
          <div key={section.title} className="cp-section" style={{ marginBottom: "20px" }}>
            <div className="cp-section-header">
              <div>
                <h2 className="cp-section-title">{section.title}</h2>
                <p className="cp-empty-text" style={{ marginTop: "4px" }}>{section.description}</p>
              </div>
            </div>
            <div className="cp-section-body">
              {section.items.map((field) => (
                <div key={field.name} className="cp-form-group">
                  <label>
                    {field.label}
                    {field.required ? " *" : ""}
                  </label>
                  {field.type === "input" ? (
                    <input
                      type="text"
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      placeholder={field.placeholder || ""}
                      required={field.required}
                    />
                  ) : (
                    <textarea
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      placeholder={field.placeholder || ""}
                      required={field.required}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {error && <p className="cp-message--error">{error}</p>}

        <div className="cp-btn-row">
          <button type="submit" className="cp-btn-primary" disabled={saving || !projectId}>
            {saving ? "Saving..." : "Save Starter Intake"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default StarterIntake;
